<script setup>
/**
 * Generic enrichment widget — renders an arbitrary enrichment blob as a set of
 * key→value pills. Used as the fallback for sideloaded / unknown enrichment
 * slugs that have no purpose-built widget in the registry.
 */
import { computed } from 'vue'

const props = defineProps({
  // The enrichment blob for one slug (an object, scalar, or array).
  data: { default: null },
  // Field prefix (the slug) for shift-select wiring on scalar leaf pills.
  field: { type: String, default: '' },
})

// Format a leaf value for display.
function fmt(value) {
  if (value === null || value === undefined) return ''
  if (Array.isArray(value)) {
    return value
      .map(v => (v && typeof v === 'object' ? JSON.stringify(v) : String(v)))
      .join(', ')
  }
  if (typeof value === 'object') return JSON.stringify(value)
  return String(value)
}

// Flatten the blob into displayable { key, value, scalar } pills.
const pills = computed(() => {
  const blob = props.data
  if (blob === null || blob === undefined) return []

  // Scalar blob (e.g. submission_count style single value).
  if (typeof blob !== 'object') {
    return [{ key: '', value: fmt(blob), scalar: true, raw: blob }]
  }

  // Array blob — index pills.
  if (Array.isArray(blob)) {
    return blob.map((v, i) => ({
      key: String(i),
      value: fmt(v),
      scalar: v == null || typeof v !== 'object',
      raw: v,
    }))
  }

  // Object blob — one pill per top-level key.
  return Object.entries(blob).map(([key, value]) => ({
    key,
    value: fmt(value),
    scalar: value == null || typeof value !== 'object',
    raw: value,
  }))
})

function pillField(pill) {
  if (!props.field) return pill.key
  return pill.key ? `${props.field}.${pill.key}` : props.field
}
</script>

<template>
  <div v-if="pills.length > 0" class="generic-widget">
    <span
      v-for="(pill, idx) in pills"
      :key="idx"
      class="generic-pill"
      :data-shift-field="pill.scalar ? pillField(pill) : null"
      :data-shift-value="pill.scalar && pill.value !== '' ? pill.value : null"
    >
      <span v-if="pill.key" class="pill-key">{{ pill.key }}</span>
      <span class="pill-value">{{ pill.value }}</span>
    </span>
  </div>
</template>

<style scoped>
.generic-widget {
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
  align-items: center;
}

.generic-pill {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  border: 1px solid var(--border-strong);
  border-radius: var(--radius-xl);
  font-size: 0.8rem;
  padding: 0 0.5rem;
  max-width: 100%;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.pill-key {
  color: var(--text-faint);
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.pill-value {
  color: var(--text-primary);
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
